import React, { useState, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormGroup,
  Label,
  Alert,
  Spinner,
} from 'reactstrap';
import { ImporterID } from 'fym-shared/src/music-services/ids';
import { musicServicesByImporterID } from 'fym-shared/src/music-services/allMusicServices';
import { TypedMemo } from '../../../common/types';
import { musicServiceImagesByImporterId } from '../../../common/musicServiceImages';
import { useTrackPlaylistByUrl } from '../../hooks/useTrackPlaylistByUrl';

const SUPPORTED_IMPORTERS: ImporterID[] = [
  ImporterID.Spotify,
  ImporterID.AppleMusic,
  ImporterID.AmazonMusic,
  ImporterID.YouTubeMusic,
  ImporterID.Youtube,
  ImporterID.Tidal,
  ImporterID.Deezer,
  ImporterID.SoundCloud,
  ImporterID.Qobuz,
  ImporterID.Napster,
  ImporterID.Pandora,
];

interface TrackPlaylistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (playlistId: string) => void;
}

const TrackPlaylistModalComponent: React.FC<TrackPlaylistModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const intl = useIntl();
  const [url, setUrl] = useState('');
  const { trackPlaylist, isPending, error, reset } = useTrackPlaylistByUrl();

  const errorMessage = error !== null ? error.message : null;

  const supportedLogos = useMemo(
    () =>
      SUPPORTED_IMPORTERS.map((importerId) => {
        const images = musicServiceImagesByImporterId[importerId];
        const Logo = images?.logoColorPadding.light;
        const service = musicServicesByImporterID[importerId];
        const name = service?.shortName ?? service?.name ?? '';
        return { importerId, Logo, name };
      }),
    []
  );

  const handleClose = useCallback(() => {
    setUrl('');
    reset();
    onClose();
  }, [onClose, reset]);

  const handleSubmit = useCallback(async () => {
    if (url.trim() === '' || isPending) {
      return;
    }

    try {
      const playlistId = await trackPlaylist(url.trim());
      setUrl('');
      reset();
      onSuccess(playlistId);
    } catch {
      // Error is handled by the mutation state
    }
  }, [url, isPending, trackPlaylist, reset, onSuccess]);

  const handleClick = useCallback(() => {
    handleSubmit().catch(console.error);
  }, [handleSubmit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !isPending) {
        handleSubmit().catch(console.error);
      }
    },
    [isPending, handleSubmit]
  );

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        {intl.formatMessage({
          id: 'app.sml.playlists.trackByUrl.title',
          defaultMessage: 'Track Playlist',
        })}
      </ModalHeader>
      <ModalBody>
        {errorMessage !== null && <Alert color="danger">{errorMessage}</Alert>}
        <FormGroup>
          <Label>
            {intl.formatMessage({
              id: 'app.sml.playlists.trackByUrl.label',
              defaultMessage: 'Playlist URL',
            })}
          </Label>
          <Input
            type="text"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder={intl.formatMessage({
              id: 'app.sml.playlists.trackByUrl.placeholder',
              defaultMessage: 'https://open.spotify.com/playlist/...',
            })}
          />
        </FormGroup>
        <div className="text-muted small mb-1">
          {intl.formatMessage({
            id: 'app.sml.playlists.trackByUrl.supportedServices',
            defaultMessage: 'Supported services',
          })}
        </div>
        <div className="d-flex flex-wrap gap-3 align-items-center justify-content-center">
          {supportedLogos.map(({ importerId, Logo, name }) => (
            <Logo key={importerId} height={40} title={name} />
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose}>
          {intl.formatMessage({
            id: 'app.sml.playlists.trackByUrl.cancel',
            defaultMessage: 'Cancel',
          })}
        </Button>
        <Button color="primary" onClick={handleClick} disabled={url.trim() === '' || isPending}>
          {isPending && <Spinner size="sm" className="me-2" />}
          {intl.formatMessage({
            id: 'app.sml.playlists.trackByUrl.submit',
            defaultMessage: 'Track',
          })}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export const TrackPlaylistModal = TypedMemo(TrackPlaylistModalComponent);
